import { DECLINE_LABEL, type TaskRow } from "@/lib/api/tasks"
import type { TaskResult } from "@/lib/tasks/task-types"

// Every sentence the task screen says, in one place. A task tells her exactly
// what to do; the line after an answer tells her exactly what happens next —
// in the pipeline's words (In Progress, Scheduled, Completed, Declined),
// never in system words.

const DAY_MS = 86_400_000

function startOfDay(d: Date): number {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x.getTime()
}

export function dayDelta(iso: string, now = new Date()): number {
  return Math.round((startOfDay(new Date(iso)) - startOfDay(now)) / DAY_MS)
}

function clock(iso: string): string {
  return new Date(iso)
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    .replace(" ", "")
    .toLowerCase()
}

function weekday(iso: string, long = false): string {
  return new Date(iso).toLocaleDateString("en-US", { weekday: long ? "long" : "short" })
}

/** "today 2:14pm" / "yesterday" / "Wed 2:14pm" / "last Fri" / "tomorrow" / "Thursday" / "Aug 12". */
export function whenLabel(iso: string, now = new Date(), withTime = true): string {
  const delta = dayDelta(iso, now)
  const t = withTime ? ` ${clock(iso)}` : ""
  if (delta === 0) return `today${t}`
  if (delta === -1) return `yesterday${t}`
  // Bare "Fri" reads as next Friday. With a time it's clearly a log line;
  // without one, say "last Fri" so an overdue date can't read as upcoming.
  if (delta > -7 && delta < 0) return withTime ? `${weekday(iso)}${t}` : `last ${weekday(iso)}`
  if (delta === 1) return "tomorrow"
  if (delta > 1 && delta < 7) return weekday(iso, true)
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

const first = (name: string) => name.split(" ")[0] || name

const slot = (iso: string) =>
  new Date(iso).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" })

// ── Due state — escalating text, never a pill ────────────────────────────
// Tone comes from the SECTION the row sits in, never from the type: a reply
// in "Done today" is done, not red.

export type DueTone = "overdue" | "now" | "quiet"

export function dueLine(row: TaskRow, now = new Date()): { text: string; tone: DueTone } {
  const tone: DueTone = row.group === "overdue" ? "overdue" : row.group === "now" ? "now" : "quiet"
  if (row.group === "done") return { text: "Done", tone: "quiet" }
  if (row.type === "reply") return { text: whenLabel(row.dueAt, now), tone }
  if (row.type === "showed") return { text: whenLabel(row.dueAt, now), tone }
  if (row.group === "overdue") {
    const days = Math.abs(dayDelta(row.dueAt, now))
    return { text: days <= 1 ? "Due yesterday" : `${days} days overdue`, tone }
  }
  if (row.group === "now") return { text: `Due ${clock(row.dueAt)}`, tone }
  if (row.group === "later") return { text: `Later today · ${clock(row.dueAt)}`, tone }
  return { text: whenLabel(row.dueAt, now, false), tone }
}

// ── After an answer: what happens next, in one line ──────────────────────

export function nextLine(result: TaskResult, row: TaskRow, now = new Date()): string {
  const name = first(row.customerName)

  switch (result.kind) {
    case "replied":
      return `Sent. The texts wait until ${name} answers.`
    case "texts_stopped":
      return `Texts stopped — ${name} is yours by phone. The calls carry on.`
    case "declined":
      return `Closed — ${DECLINE_LABEL[result.reason].toLowerCase()}. ${name} is off your list.`
    case "booked":
      return `Booked ${slot(result.at)} — Scheduled. Texts stop.`
    case "follow_up":
      return `Back to In Progress — call ${name} in ${result.inDays} days.`
    case "skipped":
      return `Moved to ${whenLabel(result.dueAt, now, false)} morning.`
    case "snoozed":
      return `Moved to ${whenLabel(result.until, now, false)}. Texts wait until then.`
    case "visit":
      if (result.rebookedAt) return `Booked again ${slot(result.rebookedAt)} — Scheduled.`
      if (result.outcome === "attended") return `${name} came in — Completed.`
      return `No-show — back to In Progress. A call is on your list.`
    case "answered": {
      const a = result.answer
      const restarted = a.kind === "reached" && a.restartDrip ? " Texts restart from the top." : ""
      if (a.kind === "confirmed") {
        return result.startedTexts ? `In Progress — texts start today. Call ${name} next.` : `Confirmed — call ${name} next.`
      }
      if (a.kind === "reached" && a.then === "booked") {
        return `Booked ${result.bookedAt ? slot(result.bookedAt) : "the slot"} — Scheduled. Texts stop.`
      }
      if (a.kind === "reached" && a.then === "declined") {
        return `Closed — ${DECLINE_LABEL[a.declineReason ?? "other"].toLowerCase()}. ${name} is off your list.`
      }
      if (a.kind === "closed") {
        return `Closed — ${DECLINE_LABEL[a.declineReason ?? "unreachable"].toLowerCase()}. ${name} is off your list.`
      }
      if (a.kind === "parked") return `Parked — ${name} is off your list until a manager wakes it.`
      const moved = row.estimateStatus === "Confirmed" && (a.kind === "reached" || a.kind === "no_answer" || a.kind === "voicemail")
        ? "Moved to In Progress — texts start today. "
        : ""
      const next = result.next
      if (!next) return `${moved}Done — nothing more on this one.`.trim()
      switch (next.type) {
        case "call":
          if (a.kind === "contact_fixed") return `Call ${name} on the new number.`
          return `${moved}Next call ${whenLabel(next.dueAt, now, false)}.${restarted}`
        case "callback":
          return `Call back ${whenLabel(next.dueAt, now, false)}. Texts wait until then.${restarted}`
        case "fix_contact":
          return `Get a working number for ${name}.`
        case "close_out":
          return `That was the last call.`
        case "reach_out":
          return `Call ${name} next.`
        case "review":
          return `Review ${name}'s estimate.`
      }
    }
  }
}
